'use client';

import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';
import { tv, type VariantProps } from 'tailwind-variants';
// biome-ignore lint/performance/noNamespaceImport: library designed for namespace import
import * as Dialog from '@/app_components/ui/primitives/Dialog';
import { usePaywall } from '@/app_lib/providers/PaywallProvider';
import { useStickyFooter } from '@/app_lib/providers/StickyFooterProvider';
import { unwrapLazy } from '@/app_lib/utils/sharedUtils';
import { cn } from '@/app_lib/utils/utils';

const modalStyles = tv({
  base: 'h-fit w-fit z-modal-content transition-transform outline-none focus:outline-none',
  variants: {
    position: {
      bottom: 'fixed bottom-0 left-0 data-[state=open]:slide-from-bottom data-[state=closed]:slide-to-bottom',
      center:
        'fixed top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%] data-[state=open]:fade-in data-[state=closed]:fade-out max-md:w-full',
      left: 'fixed top-0 left-0 data-[state=closed]:slide-to-left data-[state=open]:slide-from-left',
      right: 'fixed top-0 right-0 data-[state=open]:slide-from-right data-[state=closed]:slide-to-right',
    },
  },
});

type ModalVariants = VariantProps<typeof modalStyles>;

interface BasicModalProps extends ModalVariants {
  className?: string;
  content: React.ReactNode;
  defaultOpen?: boolean;
  openOverride?: boolean;
  onOpenChange?: (open: boolean) => void;
  description?: string;
  title?: string;
  trigger?: React.ReactNode;
}

/**
 * Basic modal component using Radix UI Dialog primitive.
 *
 * @param {string} className - Additional CSS class names applied to the dialog content container.
 * @param {React.ReactNode} content - React node rendered inside the modal body.
 * @param {boolean} defaultOpen - Initial uncontrolled open state when the component mounts.
 * @param {boolean} openOverride - Controlled open state; when defined, the modal mirrors this value.
 * @param {(open: boolean) => void} onOpenChange - Callback fired whenever the open state changes.
 * @param {string} description - Accessible description announced to screen readers.
 * @param {string} title - Accessible title announced to screen readers.
 * @param {React.ReactNode} trigger - Optional trigger element rendered as the dialog trigger (`asChild`).
 * @param {ModalVariants['position']} position - Modal position variant (inherited from `ModalVariants`) used by `modalStyles`.
 * @returns {JSX.Element} The rendered modal dialog.
 */
export const BasicModal = ({
  className,
  content,
  defaultOpen = false,
  openOverride,
  onOpenChange,
  description = 'This is an opened modal',
  title = 'Modal',
  trigger,
  position = 'center',
}: BasicModalProps) => {
  const [open, setOpen] = useState(defaultOpen);
  const { showPaywallax } = usePaywall();
  const { renderStickyFooter } = useStickyFooter();
  const pathname = usePathname();

  // biome-ignore lint/correctness/useExhaustiveDependencies: pathname is an intentional trigger to close modal on navigation
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (openOverride === undefined) {
      return;
    }
    setOpen(openOverride);
  }, [openOverride]);

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (onOpenChange) {
      onOpenChange(newOpen);
    }
    if (window.s2nApi && title === 'DrawerMenu') {
      if (!showPaywallax && renderStickyFooter) {
        window.s2nApi.float = false;
      } else {
        // When the modal is open, stn should NOT float
        // open true => float false
        // open false => float true
        window.s2nApi.float = !newOpen;
      }
    }
  };

  return (
    <Dialog.Root
      onOpenChange={handleOpenChange}
      open={open}
    >
      {trigger && (
        <Dialog.Trigger
          asChild
          tabIndex={0}
        >
          {unwrapLazy(trigger)}
        </Dialog.Trigger>
      )}
      <Dialog.Portal>
        <Dialog.Overlay
          className={cn(
            'fixed inset-0 bg-black/50 transition-opacity z-modal-backdrop',
            'data-[state=open]:fade-in data-[state=closed]:fade-out',
          )}
        />
        <Dialog.Content
          asChild
          className={cn(modalStyles({ position }), className)}
        >
          <div>
            <Dialog.Title className='sr-only'>{title}</Dialog.Title>
            <Dialog.Description className='sr-only'>{description}</Dialog.Description>
            {content}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
